import { useCallback, useEffect, useRef } from 'react';
import { ACTIVITY_WRITE_THROTTLE_MS } from './SessionTimeout.types';
import type { ExpiryReason } from './SessionTimeout.types';
import { SESSION_STORAGE_KEYS, writeLastActivity } from './sessionTimeoutStorage';

/**
 * Tracks when the customer last interacted, shared across tabs.
 *
 * ## Invariants:
 * - Holds the timestamp in a ref as well as in storage. Why: `storage` events do not fire
 *   in the tab that wrote the value, so the writer needs its own copy.
 * - Writes are throttled, reads are not. Why: mouse and key events arrive in bursts; a
 *   write per event would hammer localStorage, while the in-memory ref stays exact.
 * - A timestamp arriving from another tab is adopted only when it is NEWER. Why: the clock
 *   must move forward only, or two tabs could push each other backwards and expire a
 *   session someone is actively using.
 * - Mounting stamps "now" rather than inheriting the stored timestamp. Why: opening a tab
 *   or reloading is itself the customer being present, so the idle window restarts. This is
 *   deliberate, not an oversight - it fails toward keeping someone signed in.
 */

interface ActivityTracker {
  getLastActivity: () => number;
  markActive: (options?: { immediate?: boolean }) => void;
}

export const useActivityTracker = (
  enabled: boolean,
  onRemoteSignOut: (reason: ExpiryReason) => void
): ActivityTracker => {
  // Seeded in the mount effect rather than from a lazy initializer: reading storage and the
  // clock during render is impure. Until then `getLastActivity` reports "just now", which is
  // the safe direction — a zero would read as 1970 and expire the session instantly.
  const lastActivityRef = useRef<number>(0);
  const lastWriteRef = useRef<number>(0);
  // Held in a ref so the listener effect does not re-subscribe when the callback identity
  // changes. Assigned in an effect, never during render (`react-hooks/refs`).
  const onRemoteSignOutRef = useRef(onRemoteSignOut);
  useEffect(() => {
    onRemoteSignOutRef.current = onRemoteSignOut;
  }, [onRemoteSignOut]);

  const markActive = useCallback((options?: { immediate?: boolean }) => {
    const now = Date.now();
    lastActivityRef.current = now;
    const due = now - lastWriteRef.current >= ACTIVITY_WRITE_THROTTLE_MS;
    if (options?.immediate || due) {
      lastWriteRef.current = now;
      writeLastActivity(now);
    }
  }, []);

  const getLastActivity = useCallback(() => lastActivityRef.current || Date.now(), []);

  useEffect(() => {
    if (!enabled) return undefined;

    const onActivity = () => markActive();
    const onVisibility = () => {
      // Only a tab becoming visible counts; being hidden is not interaction.
      if (document.visibilityState === 'visible') markActive();
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === SESSION_STORAGE_KEYS.lastActivity) {
        const incoming = Number(event.newValue);
        if (Number.isFinite(incoming) && incoming > lastActivityRef.current) {
          lastActivityRef.current = incoming;
        }
        return;
      }
      if (event.key === SESSION_STORAGE_KEYS.signOutReason) {
        if (event.newValue === 'idle' || event.newValue === 'absolute') {
          onRemoteSignOutRef.current(event.newValue);
        }
      }
    };

    // Passive where the handler never calls preventDefault, and capture so activity inside
    // a scroll container still registers.
    window.addEventListener('mousedown', onActivity, { passive: true });
    window.addEventListener('keydown', onActivity, { passive: true });
    window.addEventListener('touchstart', onActivity, { passive: true });
    window.addEventListener('scroll', onActivity, { passive: true, capture: true });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('storage', onStorage);

    // Opening a tab or reloading is the customer being present, so the idle window starts
    // here rather than inheriting a stale timestamp. A tab opened and left alone still expires.
    markActive({ immediate: true });

    return () => {
      window.removeEventListener('mousedown', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('touchstart', onActivity);
      window.removeEventListener('scroll', onActivity, true);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('storage', onStorage);
    };
  }, [enabled, markActive]);

  return { getLastActivity, markActive };
};
